import { useMemo } from 'react';

import { computeGlobalBreakEvenMonth } from '@/domain/engine';
import { useProjection } from '@/hooks/useProjection';
import { formatCompact, formatNumber } from '@/lib/format';
import { useSimulatorState } from '@/store/useSimulatorState';

import { KpiCard } from '@/components/kpi/KpiCard';

function formatBreakEven(month: number | null, horizonMonths: number): string {
  if (month === null) return `Non atteint sur ${horizonMonths} mois`;
  return `Mois ${month}`;
}

export function KpiCardsRow() {
  const state = useSimulatorState();
  const horizonMonths = state.projection.horizonMonths;
  const { snapshots, finalKpis, operationalBreakEvenMonth, globalBreakEvenMonth } = useProjection();
  const monthlyCost = snapshots[0]?.monthlyCost ?? 0;
  const annualCost = monthlyCost * 12;
  // Résultat récurrent annualisé : MRR × 12 − coûts récurrents. Exclut la marge sur
  // les frais d'entrée (revente de matériel), qui n'est pas récurrente.
  const recurringAnnualResult = finalKpis.arr - annualCost;

  // Marge d'entrée (ponctuelle) annualisée au rythme du dernier mois projeté, pour
  // expliquer l'écart entre le break-even opérationnel (récurrent) et le global (total).
  const lastSnapshot = snapshots[snapshots.length - 1];
  const annualEntryMargin =
    ((lastSnapshot?.oneTimeRevenueThisMonth ?? 0) - (lastSnapshot?.oneTimeCostThisMonth ?? 0)) * 12;
  const totalAnnualResult = recurringAnnualResult + annualEntryMargin;
  const hasEntryMargin = state.tiers.some((t) => (t.entryPrice ?? 0) > 0 || (t.entryCost ?? 0) > 0);

  // Rappel : MRR/ARR/gain/clients « finaux » sont pris au dernier mois projeté.
  const horizonHint = `À l'horizon : mois ${horizonMonths}`;

  // Si le break-even global n'est pas atteint sur l'horizon, projeter au-delà
  // pour indiquer la durée qui serait néanmoins nécessaire (le cas échéant).
  const globalBreakEvenHint = useMemo(() => {
    if (globalBreakEvenMonth !== null) return undefined;
    const projectedMonth = computeGlobalBreakEvenMonth(state);
    if (projectedMonth === null) {
      return 'Jamais atteint à ce rythme';
    }
    return `Atteignable au mois ${projectedMonth} (+${projectedMonth - horizonMonths} mois)`;
  }, [globalBreakEvenMonth, state, horizonMonths]);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard label="MRR final" value={formatCompact(finalKpis.mrr)} hint={horizonHint} />
      <KpiCard label="ARR final" value={formatCompact(finalKpis.arr)} hint={horizonHint} />
      <KpiCard label="Coût mensuel" value={formatCompact(monthlyCost)} />
      <KpiCard label="Coût annuel" value={formatCompact(annualCost)} />
      {hasEntryMargin ? (
        <>
          <KpiCard
            label="Résultat annuel récurrent"
            value={formatCompact(recurringAnnualResult)}
            valueClassName={recurringAnnualResult >= 0 ? 'text-green-500' : 'text-destructive'}
            hint="MRR − coûts récurrents (hors matériel)"
          />
          <KpiCard
            label="Marge d'entrée annuelle"
            value={formatCompact(annualEntryMargin)}
            valueClassName={annualEntryMargin >= 0 ? 'text-green-500' : 'text-destructive'}
            hint="Ventes d'entrée (matériel) · à l'horizon"
          />
          <KpiCard
            label="Résultat total annuel"
            value={formatCompact(totalAnnualResult)}
            valueClassName={totalAnnualResult >= 0 ? 'text-green-500' : 'text-destructive'}
            hint="Récurrent + marge d'entrée"
          />
        </>
      ) : (
        <KpiCard
          label="Gain / Perte annuelle"
          value={formatCompact(recurringAnnualResult)}
          valueClassName={recurringAnnualResult >= 0 ? 'text-green-500' : 'text-destructive'}
          hint={horizonHint}
        />
      )}
      <KpiCard
        label="Break-even opérationnel"
        value={formatBreakEven(operationalBreakEvenMonth, horizonMonths)}
      />
      <KpiCard
        label="Break-even global"
        value={formatBreakEven(globalBreakEvenMonth, horizonMonths)}
        hint={globalBreakEvenHint}
      />
      <KpiCard
        label="Clients finaux"
        value={formatNumber(Math.round(finalKpis.totalClients))}
        hint={horizonHint}
      />
    </div>
  );
}
